import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message, Affix } from 'antd';
import moment from 'moment';
import {
    FaClock,
    FaDollarSign,
    FaHeart,
    FaRegHeart,
    FaFileAlt,
    FaInfoCircle,
    FaHome,
    FaCheckCircle,
    FaCamera,
    FaStore,
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaUsers,
    FaUser,
    FaHashtag,
    FaStar,
} from 'react-icons/fa';
import auctionService from '../../services/auctionService';
import favoriteService from '../../services/favoriteService';
import { useSelector, useDispatch } from 'react-redux';
import { toggleFavoriteLot } from '../../redux/userSlice';
import env from '../../env';
import VideoPreviewModal from '../VideoPreviewModal';
import CompanyDrawer from '../Common/CompanyDrawer';
import ShareAuctionModal from './ShareAuctionModal';
import ConnectToBidShareButtons from './ConnectToBidShareButtons';
import Breadcrumb from '../Common/Breadcrumb';
import ImageGallery from '../Common/ImageGallery';
import LeafletMap from '../Common/LeafletMap';
import './AuctionDetail.css';

// Helper function to format price
const formatPrice = (price) => {
    if (price === null || price === undefined) return '0.00';
    return Number(price).toFixed(2);
};

// Helper function to format currency with PKR
const formatCurrency = (price) => {
    return `PKR ${formatPrice(price)}`;
};

const LotDetail = () => {
    const { auctionSlug, lotSlug } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [lot, setLot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bidModalVisible, setBidModalVisible] = useState(false);
    const [videoModalVisible, setVideoModalVisible] = useState(false);
    const [companyDrawerVisible, setCompanyDrawerVisible] = useState(false);
    const [shareModalVisible, setShareModalVisible] = useState(false);
    const [currentPrice, setCurrentPrice] = useState(null);
    const [nextLot, setNextLot] = useState(null);
    const [previousLot, setPreviousLot] = useState(null);
    const [shouldStick, setShouldStick] = useState(true);
    const [countdownCompleted, setCountdownCompleted] = useState(false);
    const countdownCompleteCalled = useRef(false);
    const mainContentRef = useRef(null);
    const { currentUser } = useSelector((state) => state.user);

    useEffect(() => {
        loadLotDetails();
    }, [auctionSlug, lotSlug]);

    // Initialize countdownCompleted state when lot loads
    useEffect(() => {
        if (lot?.auction?.start_time) {
            const now = moment();
            const startTime = moment(lot.auction.start_time);
            // If auction has already started, set countdownCompleted to true
            if (startTime.isBefore(now) || startTime.isSame(now)) {
                setCountdownCompleted(true);
            } else {
                setCountdownCompleted(false);
                // Reset the ref when lot changes and hasn't started yet
                countdownCompleteCalled.current = false;
            }
        } else {
            setCountdownCompleted(false);
            countdownCompleteCalled.current = false;
        }
    }, [lot]);

    // Calculate if sticky should be enabled based on main content height and screen size
    useEffect(() => {
        const handleScroll = () => {
            if (!mainContentRef.current) return;
            
            // Disable sticky on mobile devices (screens smaller than 992px - Bootstrap lg breakpoint)
            const isMobile = window.innerWidth < 992;
            if (isMobile) {
                setShouldStick(false);
                return;
            }
            
            const mainContent = mainContentRef.current;
            const mainContentHeight = mainContent.offsetHeight;
            const mainContentTop = mainContent.offsetTop;
            const scrollPosition = window.scrollY + window.innerHeight;
            const mainContentBottom = mainContentTop + mainContentHeight;
            
            // Disable sticky when we've scrolled past the main content
            // Add some buffer (100px) to account for footer
            setShouldStick(scrollPosition < mainContentBottom - 100);
        };

        // Initial check
        handleScroll();
        
        // Check on scroll
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleScroll);
        
        // Re-check when lot data changes
        if (lot) {
            setTimeout(handleScroll, 100);
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [lot]);

    const loadLotDetails = async () => {
        try {
            setLoading(true);
            const response = await auctionService.getLot(auctionSlug, lotSlug);
            // The service returns response.data from axios
            // Backend returns: { status: 'success', data: {...lot data...} }
            // So response = { status: 'success', data: {...lot data...} }
            // We need response.data to get the actual lot data
            const lotData = response?.data || response;
            setLot(lotData);
            
            // Set current price from lot data (already included in response)
            setCurrentPrice(lotData.current_price || lotData.winning_bid?.amount || lotData.starting_price);

            // Use next/previous lot info from the API response (much more efficient!)
            if (lotData.next_lot) {
                setNextLot(lotData.next_lot);
            } else {
                setNextLot(null);
            }

            if (lotData.previous_lot) {
                setPreviousLot(lotData.previous_lot);
            } else {
                setPreviousLot(null);
            }
        } catch (error) {
            console.error('Error loading lot details:', error);
            message.error('Failed to load lot details');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleFavorite = async () => {
        if (!currentUser) {
            message.warning('Please login to add favorites');
            return;
        }

        // Optimistically update Redux state
        dispatch(toggleFavoriteLot(lot.id));

        try {
            await favoriteService.toggleFavoriteLot(lot.id);
        } catch (error) {
            console.error('Error toggling favorite:', error);
            // Revert the optimistic update on error
            dispatch(toggleFavoriteLot(lot.id));
        }
    };

    const isFavorite = currentUser?.favorite_lots?.includes(lot?.id);

    // Build dynamic breadcrumb items
    const buildBreadcrumbItems = () => {
        const items = [
            { label: 'Home', to: '/' },
            { label: 'Auctions', to: '/auctions' }
        ];

        // Add category if available
        if (lot?.auction?.category) {
            items.push({
                label: lot.auction.category.name || 'Category',
                to: lot.auction.category.slug ? `/category-results?category=${lot.auction.category.slug}` : `/auctions?category=${lot.auction.category.id}`
            });
        }

        // Add auction
        if (lot?.auction) {
            items.push({
                label: lot.auction.title || auctionSlug,
                to: `/auctions/${auctionSlug}`
            });
        }

        // Add current lot
        if (lot) {
            items.push({
                label: lot.title || 'Lot Details',
                active: true
            });
        }

        return items;
    };

    // Handle next lot navigation
    const handleNextLot = () => {
        if (nextLot && nextLot.slug) {
            navigate(`/auctions/${auctionSlug}/lots/${nextLot.slug}`);
        }
    };

    // Handle previous lot navigation
    const handlePreviousLot = () => {
        if (previousLot && previousLot.slug) {
            navigate(`/auctions/${auctionSlug}/lots/${previousLot.slug}`);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'secondary',
            active: 'success',
            sold: 'success',
            unsold: 'danger'
        };
        return colors[status] || 'secondary';
    };

    const getStatusBadgeClass = (status) => {
        const color = getStatusColor(status);
        return `bg-${color}`;
    };

    const getDisplayStatus = (status) => {
        if (status?.toLowerCase() === 'closed') {
            return 'sold';
        }
        return status;
    };

    const renderSpecifications = () => (
        <div>
            <div className="d-flex align-items-center mb-4">
                <div className="bg-blue-50 rounded-2 p-2 me-3" style={{
                    backgroundColor: '#eff6ff'
                }}>
                    <FaFileAlt style={{ fontSize: '20px', color: '#3b82f6' }} />
                </div>
                <h3 className="mb-0 fw-bold" style={{ 
                    color: '#1e293b', 
                    fontSize: '20px',
                    letterSpacing: '-0.3px'
                }}>
                    Specifications
                </h3>
            </div>
            {lot.specifications.map((group, groupIndex) => (
                <div 
                    key={groupIndex} 
                    className={groupIndex < lot.specifications.length - 1 ? 'mb-4' : ''}
                >
                    <div className="mb-3 pb-2" style={{
                        borderBottom: '2px solid #e2e8f0'
                    }}>
                        <h4 className="mb-0 fw-bold" style={{ 
                            color: '#1e293b', 
                            fontSize: '18px',
                            letterSpacing: '-0.2px'
                        }}>
                            {group.heading}
                        </h4>
                    </div>
                    <div className="card border-0" style={{
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden'
                    }}>
                        <div className="table-responsive">
                            <table className="table mb-0" style={{ 
                                borderRadius: '12px',
                                overflow: 'hidden'
                            }}>
                                <tbody>
                                    {group.fields.map((field, fieldIndex) => {
                                        const [key, value] = Object.entries(field)[0];
                                        const formattedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
                                        const isLastRow = fieldIndex === group.fields.length - 1;
                                        
                                        return (
                                            <tr 
                                                key={fieldIndex} 
                                                style={{ 
                                                    transition: 'all 0.2s ease',
                                                    backgroundColor: fieldIndex % 2 === 0 ? '#ffffff' : '#f8fafc'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#eff6ff';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = fieldIndex % 2 === 0 ? '#ffffff' : '#f8fafc';
                                                }}
                                            >
                                                <th style={{ 
                                                    width: '35%', 
                                                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                                    fontWeight: '600',
                                                    color: '#475569',
                                                    padding: '14px 16px',
                                                    border: 'none',
                                                    borderBottom: isLastRow ? 'none' : '1px solid #e2e8f0',
                                                    fontSize: '13px',
                                                    letterSpacing: '0.3px',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {formattedKey}
                                                </th>
                                                <td style={{ 
                                                    padding: '14px 16px',
                                                    border: 'none',
                                                    borderBottom: isLastRow ? 'none' : '1px solid #e2e8f0',
                                                    color: '#1e293b',
                                                    fontSize: '15px',
                                                    fontWeight: '500',
                                                    lineHeight: '1.6'
                                                }}>
                                                    {String(value) || 'N/A'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderSkeleton = () => {
        // Skeleton animation style
        const skeletonAnimation = {
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'loading 1.5s ease-in-out infinite'
        };

        return (
            <div className="lot-detail" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
                {/* Breadcrumb Skeleton */}
                <div className="container-fluid px-2 px-lg-3" style={{ paddingTop: '16px', paddingBottom: '16px' }}>
                    <div style={{
                        height: '60px',
                        borderRadius: '12px',
                        ...skeletonAnimation
                    }}></div>
                </div>

                {/* Main Content Skeleton */}
                <div className="container-fluid px-2 px-lg-3" style={{ paddingTop: '16px', paddingBottom: '24px' }}>
                    <div className="row g-3">
                        {/* Left Column - Images & Details */}
                    <div className="col-lg-8">
                            <div className="d-flex flex-column gap-3">
                                {/* Auction Details Card Skeleton - Left */}
                                <div className="card border-0" style={{ 
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                                }}>
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                            <div className="d-flex align-items-center">
                                                <div style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '8px',
                                                    ...skeletonAnimation,
                                                    marginRight: '8px'
                                                }}></div>
                                                <div>
                                                    <div style={{
                                                        width: '60px',
                                                        height: '10px',
                                                        borderRadius: '4px',
                                                        ...skeletonAnimation,
                                                        marginBottom: '6px'
                                                    }}></div>
                                                    <div style={{
                                                        width: '200px',
                                                        height: '18px',
                                                        borderRadius: '4px',
                                                        ...skeletonAnimation
                                                    }}></div>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-3">
                                                <div style={{
                                                    width: '80px',
                                                    height: '24px',
                                                    borderRadius: '4px',
                                                    ...skeletonAnimation
                                                }}></div>
                                                <div style={{
                                                    width: '100px',
                                                    height: '40px',
                                                    borderRadius: '4px',
                                                    ...skeletonAnimation
                                                }}></div>
                                                <div style={{
                                                    width: '100px',
                                                    height: '40px',
                                                    borderRadius: '4px',
                                                    ...skeletonAnimation
                                                }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Images Section Skeleton */}
                                <div className="card border-0" style={{ 
                                    borderRadius: '16px', 
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0'
                                }}>
                            <div style={{
                                width: '100%',
                                        height: '500px',
                                        ...skeletonAnimation
                                    }}></div>
                                </div>

                                {/* Description Section Skeleton */}
                                <div className="card border-0" style={{ 
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <div className="card-body p-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <div style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '8px',
                                                ...skeletonAnimation,
                                                marginRight: '12px'
                                            }}></div>
                                            <div style={{
                                                width: '150px',
                                                height: '24px',
                                                borderRadius: '4px',
                                                ...skeletonAnimation
                                            }}></div>
                                        </div>
                                        <div className="p-4 rounded-3" style={{ 
                                            backgroundColor: '#f8fafc', 
                                            border: '1px solid #e2e8f0'
                                        }}>
                                            <div style={{
                                                width: '100%',
                                                height: '16px',
                                                borderRadius: '4px',
                                                ...skeletonAnimation,
                                                marginBottom: '12px'
                                            }}></div>
                                            <div style={{
                                                width: '90%',
                                                height: '16px',
                                                borderRadius: '4px',
                                                ...skeletonAnimation,
                                                marginBottom: '12px'
                                            }}></div>
                                            <div style={{
                                                width: '85%',
                                                height: '16px',
                                                borderRadius: '4px',
                                                ...skeletonAnimation
                                            }}></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Specifications Section Skeleton */}
                                <div className="card border-0" style={{ 
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <div className="card-body p-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <div style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '8px',
                                                ...skeletonAnimation,
                                                marginRight: '12px'
                                            }}></div>
                                            <div style={{
                                                width: '180px',
                                                height: '24px',
                                                borderRadius: '4px',
                                                ...skeletonAnimation
                                            }}></div>
                                        </div>
                                        <div className="row g-2">
                                            {[1, 2, 3, 4].map((item) => (
                                                <div key={item} className="col-6">
                                                    <div className="p-3 rounded-3" style={{
                                                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                                        border: '1px solid #e2e8f0'
                                                    }}>
                                                        <div style={{
                                                            width: '60px',
                                                            height: '16px',
                                                            borderRadius: '4px',
                                                            ...skeletonAnimation,
                                                            marginBottom: '8px'
                                                        }}></div>
                                <div style={{
                                    width: '80px',
                                                            height: '20px',
                                                            borderRadius: '4px',
                                                            ...skeletonAnimation
                                                        }}></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Location Map Section Skeleton */}
                                <div className="card border-0" style={{ 
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <div className="card-body p-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <div style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '8px',
                                                ...skeletonAnimation,
                                                marginRight: '12px'
                                            }}></div>
                                            <div style={{
                                                width: '120px',
                                                height: '24px',
                                                borderRadius: '4px',
                                                ...skeletonAnimation
                                            }}></div>
                                </div>
                                        <div style={{
                                            width: '100%',
                                            height: '400px',
                                            borderRadius: '12px',
                                            border: '1px solid #e2e8f0',
                                            ...skeletonAnimation
                                        }}></div>
                            </div>
                        </div>
                    </div>
                        </div>

                        {/* Right Column - Price & Overview */}
                    <div className="col-lg-4">
                            <div className="d-flex flex-column gap-3">
                                {/* Price Card Skeleton */}
                                <div className="card border-0" style={{ 
                                    borderRadius: '16px', 
                                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', 
                                    border: '2px solid #93c5fd !important',
                                    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
                                    zIndex: 10
                                }}>
                                    <div className="card-body p-4">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <div style={{
                                                    width: '100px',
                                                    height: '12px',
                                                    borderRadius: '4px',
                                                    ...skeletonAnimation,
                                                    marginBottom: '8px'
                                                }}></div>
                                                <div style={{
                                                    width: '150px',
                                                    height: '38px',
                                                    borderRadius: '4px',
                                                    ...skeletonAnimation
                                                }}></div>
                            </div>
                                            <div className="text-end">
                                                <div style={{
                                                    width: '80px',
                                                    height: '12px',
                                                    borderRadius: '4px',
                                                    ...skeletonAnimation,
                                                    marginBottom: '8px'
                                                }}></div>
                                                <div style={{
                                                    width: '100px',
                                                    height: '24px',
                                                    borderRadius: '4px',
                                                    ...skeletonAnimation,
                                                    marginBottom: '16px'
                                                }}></div>
                                                <div style={{
                                                    width: '80px',
                                                    height: '12px',
                                                    borderRadius: '4px',
                                                    ...skeletonAnimation,
                                                    marginBottom: '8px'
                                                }}></div>
                                                <div style={{
                                                    width: '100px',
                                                    height: '24px',
                                                    borderRadius: '4px',
                                                    ...skeletonAnimation
                                                }}></div>
                        </div>
                    </div>
                                        <div style={{
                                            width: '100%',
                                            height: '56px',
                                            borderRadius: '12px',
                                            ...skeletonAnimation
                                        }}></div>
                </div>
            </div>

                                {/* Auction Info Card Skeleton - Right */}
                                <div className="card border-0" style={{ 
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0',
                                    zIndex: 9
                                }}>
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                            <div style={{
                                                width: '120px',
                                                height: '40px',
                                                borderRadius: '4px',
                                                ...skeletonAnimation
                                            }}></div>
                                            <div style={{
                                                width: '100px',
                                                height: '40px',
                                                borderRadius: '4px',
                                                ...skeletonAnimation
                                            }}></div>
                                            <div style={{
                                                width: '60px',
                                                height: '40px',
                                                borderRadius: '4px',
                                                ...skeletonAnimation
                                            }}></div>
                                            <div style={{
                                                width: '80px',
                                                height: '40px',
                                                borderRadius: '4px',
                                                ...skeletonAnimation
                                            }}></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Overview Card Skeleton */}
                                <div className="card border-0" style={{ 
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0',
                                    zIndex: 9
                                }}>
                                    <div className="card-body p-4">
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                            <div style={{
                                                width: '120px',
                                                height: '24px',
                                                borderRadius: '4px',
                                                ...skeletonAnimation
                                            }}></div>
                                        </div>

                                        {/* Starting Price Featured Box Skeleton */}
                                        <div className="p-4 mb-3 rounded-3" style={{
                                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                            border: 'none',
                                            boxShadow: '0 4px 16px rgba(37, 99, 235, 0.25)'
                                        }}>
                                            <div style={{
                                                width: '120px',
                                                height: '12px',
                                                borderRadius: '4px',
                                                background: 'rgba(255, 255, 255, 0.3)',
                                                marginBottom: '12px'
                                            }}></div>
                                            <div style={{
                                                width: '180px',
                                                height: '36px',
                                                borderRadius: '4px',
                                                background: 'rgba(255, 255, 255, 0.3)'
                                            }}></div>
                                        </div>

                                        {/* Info Grid Skeleton */}
                                        <div className="row g-2 mb-3">
                                            {[1, 2, 3, 4].map((item) => (
                                                <div key={item} className="col-6">
                                                    <div className="p-3 rounded-3 h-100" style={{
                                                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                                        border: '1px solid #e2e8f0',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                                    }}>
                                                        <div style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '8px',
                                                            ...skeletonAnimation,
                                                            marginBottom: '8px'
                                                        }}></div>
                                                        <div style={{
                                                            width: '60px',
                                                            height: '12px',
                                                            borderRadius: '4px',
                                                            ...skeletonAnimation,
                                                            marginBottom: '8px'
                                                        }}></div>
                                                        <div style={{
                                                            width: '40px',
                                                            height: '22px',
                                                            borderRadius: '4px',
                                                            ...skeletonAnimation
                                                        }}></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add CSS animation */}
                <style>{`
                    @keyframes loading {
                        0% {
                            background-position: 200% 0;
                        }
                        100% {
                            background-position: -200% 0;
                        }
                    }
                `}</style>
        </div>
    );
    };

    if (loading) {
        return renderSkeleton();
    }

    if (!lot) {
        return (
            <div className="container py-5">
                <div className="text-center">
                    <h3 className="text-muted">Lot not found</h3>
                </div>
            </div>
        );
    }

    return (
        <div className="lot-detail" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Breadcrumb Navigation Section */}
                    <Breadcrumb
                items={buildBreadcrumbItems()}
                backLink={previousLot ? {
                    to: `/auctions/${auctionSlug}/lots/${previousLot.slug}`,
                    label: '← Previous Lot',
                    onClick: (e) => {
                        e.preventDefault();
                        handlePreviousLot();
                    }
                } : {
                            to: `/auctions/${auctionSlug}`,
                    label: '← Back to Auction'
                        }}
                nextLink={nextLot ? {
                    to: `/auctions/${auctionSlug}/lots/${nextLot.slug}`,
                            label: 'Next lot →',
                            onClick: (e) => {
                                e.preventDefault();
                        handleNextLot();
                    }
                } : null}
                lot={lot}
                auctionSlug={auctionSlug}
                isFavorite={isFavorite}
                onToggleFavorite={handleToggleFavorite}
                onShareClick={() => setShareModalVisible(true)}
            />

            {/* Main Content Area */}
            <div className="container-fluid px-2 px-lg-3" style={{ paddingTop: '16px', paddingBottom: '24px' }}>
                <div className="row g-3" ref={mainContentRef}>
                    {/* Left Column - Images */}
                    <div className="col-lg-8">
                        <div className="d-flex flex-column gap-3">
                            {/* Auction Details Card - Left */}
                            {lot?.auction && (
                                <div className="card border-0" style={{ 
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                                }}>
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-blue-50 rounded-2 p-2 me-2" style={{
                                                    backgroundColor: '#eff6ff'
                                                }}>
                                                    <FaStore style={{ fontSize: '16px', color: '#3b82f6' }} />
                                                </div>
                                                <div>
                                                    <small className="text-uppercase fw-semibold d-block" style={{ 
                                                        fontSize: '10px', 
                                                        letterSpacing: '0.8px',
                                                        color: '#64748b'
                                                    }}>
                                                        Auction
                                                    </small>
                                                    <h6 
                                                        className="mb-0 fw-bold" 
                                                        style={{ 
                                                            fontSize: '15px', 
                                                            color: '#3b82f6',
                                                            lineHeight: '1.2',
                                                            cursor: 'pointer',
                                                            transition: 'color 0.2s ease'
                                                        }}
                                                        onClick={() => navigate(`/auctions/${auctionSlug}`)}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.color = '#2563eb';
                                                            e.currentTarget.style.textDecoration = 'underline';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.color = '#3b82f6';
                                                            e.currentTarget.style.textDecoration = 'none';
                                                        }}
                                                    >
                                                        {lot.auction.title || 'N/A'}
                                                    </h6>
                                                </div>
                                            </div>
                                            
                                            <div className="d-flex align-items-center gap-3">
                                                {lot.auction.category && (
                                                    <div className="d-flex align-items-center">
                                                        <FaHashtag style={{ fontSize: '14px', color: '#3b82f6', marginRight: '6px' }} />
                                                        <div>
                                                            <small className="text-uppercase fw-semibold d-block" style={{ 
                                                                fontSize: '9px', 
                                                                letterSpacing: '0.8px',
                                                                color: '#64748b'
                                                            }}>
                                                                Category
                                                            </small>
                                                            <p 
                                                                className="mb-0 fw-semibold" 
                                                                style={{ 
                                                                    fontSize: '12px', 
                                                                    color: '#1e293b',
                                                                    lineHeight: '1.2',
                                                                    cursor: 'pointer',
                                                                    transition: 'color 0.2s ease'
                                                                }}
                                                                onClick={() => {
                                                                    const categoryUrl = lot.auction.category.slug 
                                                                        ? `/category-results?category=${lot.auction.category.slug}` 
                                                                        : `/auctions?category=${lot.auction.category.id}`;
                                                                    navigate(categoryUrl);
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.color = '#3b82f6';
                                                                    e.currentTarget.style.textDecoration = 'underline';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.color = '#1e293b';
                                                                    e.currentTarget.style.textDecoration = 'none';
                                                                }}
                                                            >
                                                                {lot.auction.category.name || lot.auction.category}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {lot.auction.status && lot.auction.status.toLowerCase() !== 'active' && (
                                                    <div className="text-center">
                                                        <span className={`badge px-3 py-2 ${getStatusBadgeClass(lot.auction.status)}`} style={{ 
                                                            fontSize: '11px',
                                                            fontWeight: '600'
                                                        }}>
                                                            {lot.auction.status?.toUpperCase() || 'N/A'}
                                                        </span>
                                                    </div>
                                                )}
                                                
                                                {lot.auction.start_time && (
                                                    <div className="d-flex align-items-center">
                                                        <FaClock style={{ fontSize: '14px', color: '#3b82f6', marginRight: '6px' }} />
                                                        <div>
                                                            <small className="text-uppercase fw-semibold d-block" style={{ 
                                                                fontSize: '9px', 
                                                                letterSpacing: '0.8px',
                                                                color: '#64748b'
                                                            }}>
                                                                Start
                                                            </small>
                                                            <p className="mb-0 fw-semibold" style={{ 
                                                                fontSize: '12px', 
                                                                color: '#1e293b',
                                                                lineHeight: '1.2'
                                                            }}>
                                                                {moment(lot.auction.start_time).format('MMM DD, hh:mm A')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {lot.auction.end_time && (
                                                    <div className="d-flex align-items-center">
                                                        <FaClock style={{ fontSize: '14px', color: '#3b82f6', marginRight: '6px' }} />
                                                        <div>
                                                            <small className="text-uppercase fw-semibold d-block" style={{ 
                                                                fontSize: '9px', 
                                                                letterSpacing: '0.8px',
                                                                color: '#64748b'
                                                            }}>
                                                                End
                                                            </small>
                                                            <p className="mb-0 fw-semibold" style={{ 
                                                                fontSize: '12px', 
                                                                color: '#1e293b',
                                                                lineHeight: '1.2'
                                                            }}>
                                                                {moment(lot.auction.end_time).format('MMM DD, hh:mm A')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Images Section */}
                        <div className="card border-0" style={{ 
                            borderRadius: '16px', 
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid #e2e8f0'
                        }}>
                            <div className="card-body p-0">
                                <ImageGallery
                                    images={lot?.images || []}
                                    title={lot?.title || ''}
                                    videoUrl={lot?.video_url}
                                    onVideoClick={() => setVideoModalVisible(true)}
                                    status={lot?.status}
                                    statusBadgeClass={getStatusBadgeClass(lot?.status)}
                                    isFavorite={isFavorite}
                                    onToggleFavorite={handleToggleFavorite}
                    />
                </div>
            </div>

                            {/* Overview Section - Mobile Only */}
                            <div className="card border-0 d-block d-lg-none" style={{ 
                                borderRadius: '16px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid #e2e8f0',
                                zIndex: 9
                            }}>
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <h5 className="card-title mb-0 fw-bold" style={{ 
                                            fontSize: '20px', 
                                            color: '#1e293b',
                                            letterSpacing: '-0.3px'
                                        }}>
                                            <FaInfoCircle className="me-2" style={{ color: '#3b82f6' }} />
                                            Overview
                                        </h5>
                                    </div>

                                    {/* Starting Price - Featured */}
                                    <div className="p-4 mb-3 rounded-3" style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                        border: 'none',
                                        boxShadow: '0 4px 16px rgba(37, 99, 235, 0.25)'
                                    }}>
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <small className="text-white text-uppercase fw-semibold d-block mb-2" style={{ 
                                                    fontSize: '11px', 
                                                    letterSpacing: '1px', 
                                                    opacity: 0.95 
                                                }}>
                                                    Starting Price
                                                </small>
                                                <h3 className="mb-0 fw-bold text-white" style={{ 
                                                    fontSize: '30px', 
                                                    textShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                    letterSpacing: '-0.5px'
                                                }}>
                                                    {formatCurrency(lot.starting_price)}
                                                </h3>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Info Grid */}
                                    <div className="row g-2 mb-3">
                                        {/* Quantity */}
                                        <div className="col-6">
                                            <div className="p-3 rounded-3 h-100" style={{
                                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                                border: '1px solid #e2e8f0',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                            }}>
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="bg-blue-50 rounded-2 p-2" style={{
                                                        backgroundColor: '#eff6ff'
                                                    }}>
                                                        <FaHashtag style={{ fontSize: '16px', color: '#3b82f6' }} />
                                                    </div>
                                                </div>
                                                <small className="text-uppercase fw-semibold d-block mb-2" style={{ 
                                                    fontSize: '10px', 
                                                    letterSpacing: '0.8px',
                                                    color: '#64748b'
                                                }}>
                                                    Quantity
                                                </small>
                                                <h5 className="mb-0 fw-bold" style={{ 
                                                    fontSize: '22px', 
                                                    color: '#1e293b',
                                                    letterSpacing: '-0.3px'
                                                }}>
                                                    {lot.quantity}
                                                </h5>
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <div className="col-6">
                                            <div className={`p-3 rounded-3 h-100`} style={{
                                                background: getStatusColor(lot.status) === 'success'
                                                    ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                                                    : getStatusColor(lot.status) === 'danger'
                                                    ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                                                    : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                                                border: `2px solid ${getStatusColor(lot.status) === 'success' ? '#10b981' : getStatusColor(lot.status) === 'danger' ? '#ef4444' : '#cbd5e1'}`,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                            }}>
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="rounded-2 p-2" style={{
                                                        backgroundColor: getStatusColor(lot.status) === 'success' 
                                                            ? 'rgba(16, 185, 129, 0.15)' 
                                                            : getStatusColor(lot.status) === 'danger'
                                                            ? 'rgba(239, 68, 68, 0.15)'
                                                            : 'rgba(148, 163, 184, 0.15)'
                                                    }}>
                                                        <FaCheckCircle style={{ 
                                                            fontSize: '16px',
                                                            color: getStatusColor(lot.status) === 'success' ? '#10b981' : getStatusColor(lot.status) === 'danger' ? '#ef4444' : '#64748b'
                                                        }} />
                                                    </div>
                                                </div>
                                                <small className={`text-uppercase fw-semibold d-block mb-2`} style={{ 
                                                    fontSize: '10px', 
                                                    letterSpacing: '0.8px',
                                                    color: getStatusColor(lot.status) === 'success' ? '#059669' : getStatusColor(lot.status) === 'danger' ? '#dc2626' : '#64748b'
                                                }}>
                                                    Status
                                                </small>
                                                <h6 className="mb-0 fw-bold text-capitalize" style={{
                                                    fontSize: '18px',
                                                    color: getStatusColor(lot.status) === 'success' ? '#047857' : getStatusColor(lot.status) === 'danger' ? '#b91c1c' : '#334155',
                                                    letterSpacing: '-0.2px'
                                                }}>
                                                    {getDisplayStatus(lot.status)}
                                                </h6>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Details Section */}
                                    <div className="border-top pt-3 mt-3" style={{ borderColor: '#e2e8f0' }}>
                                        {/* Last Bid Time */}
                                        {lot.last_bid_time && (
                                            <div className="d-flex align-items-center mb-2 p-3 rounded-3" style={{ 
                                                backgroundColor: '#f8fafc',
                                                border: '1px solid #e2e8f0'
                                            }}>
                                                <div className="bg-blue-50 rounded-2 p-2 me-3" style={{
                                                    backgroundColor: '#eff6ff'
                                                }}>
                                                    <FaClock style={{ fontSize: '16px', color: '#3b82f6' }} />
                                                </div>
                                                <div className="flex-grow-1">
                                                    <small className="text-uppercase fw-semibold d-block mb-1" style={{ 
                                                        fontSize: '10px', 
                                                        letterSpacing: '0.8px',
                                                        color: '#64748b'
                                                    }}>
                                                        Last Bid Time
                                                    </small>
                                                    <span className="fw-semibold" style={{ 
                                                        fontSize: '15px', 
                                                        color: '#1e293b'
                                                    }}>
                                                        {moment(lot.last_bid_time).format('MMM DD, YYYY HH:mm')}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Has Active Bids */}
                                        {lot.has_bid && (
                                            <div className="d-flex align-items-center p-3 rounded-3" style={{
                                                background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                                                border: '2px solid #10b981',
                                                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)'
                                            }}>
                                                <div className="bg-green-50 rounded-2 p-2 me-3" style={{
                                                    backgroundColor: 'rgba(16, 185, 129, 0.15)'
                                                }}>
                                                    <FaCheckCircle style={{ fontSize: '16px', color: '#10b981' }} />
                                                </div>
                                                <div className="flex-grow-1">
                                                    <small className="text-uppercase fw-semibold d-block mb-1" style={{ 
                                                        fontSize: '10px', 
                                                        letterSpacing: '0.8px',
                                                        color: '#059669'
                                                    }}>
                                                        Active Bids
                                                    </small>
                                                    <span className="fw-bold" style={{ 
                                                        fontSize: '18px',
                                                        color: '#047857'
                                                    }}>
                                                        Yes
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
            
                            {/* Description Section */}
                            <div className="card border-0" style={{ 
                                borderRadius: '16px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid #e2e8f0'
                            }}>
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="bg-blue-50 rounded-2 p-2 me-3" style={{
                                            backgroundColor: '#eff6ff'
                                        }}>
                                            <FaFileAlt style={{ fontSize: '18px', color: '#3b82f6' }} />
                                        </div>
                                        <h3 className="mb-0 fw-bold" style={{ 
                                            fontSize: '20px',
                                            color: '#1e293b',
                                            letterSpacing: '-0.3px'
                                        }}>
                                            Description
                                        </h3>
                                    </div>
                                    <div className="p-4 rounded-3" style={{ 
                                        backgroundColor: '#f8fafc', 
                                        border: '1px solid #e2e8f0',
                                        lineHeight: '1.8'
                                    }}>
                                        <p className="mb-0" style={{ 
                                            fontSize: '16px', 
                                            color: '#475569',
                                            lineHeight: '1.8'
                                        }}>
                                            {lot.description || 'No description available.'}
                                        </p>
                            </div>
                        </div>
                    </div>

                            {/* Specifications */}
                            {Array.isArray(lot.specifications) && lot.specifications.length > 0 && (
                                <div className="card border-0" style={{ 
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <div className="card-body p-4">
                                        {renderSpecifications()}
                                    </div>
                                </div>
                            )}

                            {/* Location Map */}
                            <div className="card border-0" style={{ 
                                borderRadius: '16px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid #e2e8f0'
                            }}>
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="bg-blue-50 rounded-2 p-2 me-3" style={{
                                            backgroundColor: '#eff6ff'
                                        }}>
                                            <FaMapMarkerAlt style={{ fontSize: '18px', color: '#3b82f6' }} />
                                        </div>
                                        <h3 className="mb-0 fw-bold" style={{
                                            fontSize: '20px',
                                            color: '#1e293b',
                                            letterSpacing: '-0.4px'
                                        }}>
                                            Location
                                        </h3>
                                    </div>
                                    
                                    {lot.auction?.location ? (
                                        <>
                                            <div className="mb-3">
                                                <p className="mb-0" style={{
                                                    fontSize: '15px',
                                                    color: '#475569',
                                                    fontWeight: '500'
                                                }}>
                                                    {lot.auction.location}
                                                </p>
                                            </div>
                                            <LeafletMap
                                                location={lot.auction.location}
                                                height="400px"
                                                zoom={13}
                                                showOpenInMaps={true}
                                            />
                                        </>
                                    ) : (
                                        <div style={{
                                            width: '100%',
                                            height: '400px',
                                            borderRadius: '12px',
                                            border: '1px solid #e2e8f0',
                                            backgroundColor: '#f8fafc',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#64748b'
                                        }}>
                                            <p className="mb-0" style={{ fontSize: '14px' }}>
                                                Location not available
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Agents Section */}
                            {lot.auction?.agents && lot.auction.agents.length > 0 && (
                                <div className="card border-0" style={{ 
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                                }}>
                                    <div className="card-body p-4">
                                        {/* Header */}
                                        <div className="d-flex align-items-center justify-content-between mb-4">
                                            <div className="d-flex align-items-center">
                                                <div className="bg-blue-50 rounded-2 p-2 me-3" style={{
                                                    backgroundColor: '#eff6ff'
                                                }}>
                                                    <FaUsers style={{ fontSize: '20px', color: '#3b82f6' }} />
                                            </div>
                                            <div>
                                                    <h5 className="mb-0 fw-bold" style={{ 
                                                        color: '#1e293b', 
                                                        fontSize: '20px', 
                                                        letterSpacing: '-0.3px' 
                                                    }}>
                                                        Contact Agents
                                                </h5>
                                                    <small className="text-muted" style={{ fontSize: '13px' }}>
                                                        {lot.auction.agents.length} {lot.auction.agents.length === 1 ? 'agent' : 'agents'} available to assist you
                                                </small>
                                            </div>
                                            </div>
                                       
                                        </div>

                                        {/* Agents List */}
                                        <div className="row g-3">
                                            {lot.auction.agents.map((agent, index) => (
                                                <div 
                                                    key={agent.id}
                                                    className="col-md-6"
                                                >
                                                    <div 
                                                        className="p-4 rounded-3 h-100 position-relative" 
                                                        style={{ 
                                                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                                            border: '2px solid #e2e8f0',
                                                            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(-4px)';
                                                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.15)';
                                                            e.currentTarget.style.borderColor = '#93c5fd';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                                        }}
                                                    >
                                                        {/* Agent Card Content */}
                                                        <div>
                                                            <div className="d-flex align-items-start mb-3">
                                                        {/* Profile Picture */}
                                                                <div className="me-3" style={{ flexShrink: 0, position: 'relative' }}>
                                                            {agent.photo_url ? (
                                                                <img
                                                                    src={agent.photo_url.startsWith('http')
                                                                        ? agent.photo_url
                                                                        : env.BACKEND_URL + agent.photo_url}
                                                                    alt={agent.agent_name}
                                                                    className="rounded-circle"
                                                                    style={{
                                                                                width: '64px',
                                                                                height: '64px',
                                                                        objectFit: 'cover',
                                                                                border: '3px solid #ffffff',
                                                                                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
                                                                    }}
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        if (e.target.nextSibling) {
                                                                            e.target.nextSibling.style.display = 'flex';
                                                                        }
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div
                                                                className={`rounded-circle ${agent.photo_url ? 'd-none' : 'd-flex'} align-items-center justify-content-center text-white fw-bold`}
                                                                style={{
                                                                            width: '64px',
                                                                            height: '64px',
                                                                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                                                            border: '3px solid #ffffff',
                                                                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                                                                            fontSize: '28px'
                                                                }}
                                                            >
                                                                {agent.agent_name?.charAt(0).toUpperCase()}
                                                            </div>
                                                                    {/* Online Status Indicator */}
                                                                    <div style={{
                                                                        position: 'absolute',
                                                                        bottom: '2px',
                                                                        right: '2px',
                                                                        width: '16px',
                                                                        height: '16px',
                                                                        borderRadius: '50%',
                                                                        backgroundColor: '#10b981',
                                                                        border: '3px solid #ffffff',
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                                    }}></div>
                            </div>
                            
                                                        {/* Agent Info */}
                                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                                    <h6 className="mb-1 fw-bold" style={{ 
                                                                        fontSize: '18px', 
                                                                        color: '#1e293b',
                                                                        letterSpacing: '-0.3px',
                                                                        marginBottom: '6px'
                                }}>
                                                                        {agent.agent_name}
                                                                    </h6>
                                                                    {agent.designation && (
                                                                        <p className="mb-0" style={{ 
                                                                            fontSize: '14px',
                                                                            color: '#64748b',
                                                                            fontWeight: '500',
                                                                            marginBottom: '12px'
                                                                        }}>
                                                                            {agent.designation}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Quick Action Buttons */}
                                                            <div className="d-flex gap-2 mb-3">
                                                                {agent.phone_whatsapp && (
                                                                    <a 
                                                                        href={`tel:${agent.phone_whatsapp}`}
                                                                        className="btn btn-sm flex-grow-1"
                                                                        style={{
                                                                            backgroundColor: '#3b82f6',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            borderRadius: '8px',
                                                                            fontSize: '13px',
                                                                            fontWeight: '600',
                                                                            padding: '8px 12px',
                                                                            textDecoration: 'none',
                                                                            transition: 'all 0.2s ease',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            gap: '6px'
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.backgroundColor = '#2563eb';
                                                                            e.currentTarget.style.transform = 'scale(1.02)';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.backgroundColor = '#3b82f6';
                                                                            e.currentTarget.style.transform = 'scale(1)';
                                                                        }}
                                                                    >
                                                                        <FaPhone style={{ fontSize: '12px' }} />
                                                                        Call
                                                                    </a>
                                                                )}
                                                                {agent.email && (
                                                                    <a 
                                                                        href={`mailto:${agent.email}`}
                                                                        className="btn btn-sm flex-grow-1"
                                                                        style={{
                                                                            backgroundColor: '#10b981',
                                                                            color: 'white',
                                                                            border: 'none',
                                                                            borderRadius: '8px',
                                                                            fontSize: '13px',
                                                                            fontWeight: '600',
                                                                            padding: '8px 12px',
                                                                            textDecoration: 'none',
                                                                            transition: 'all 0.2s ease',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            gap: '6px'
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.backgroundColor = '#059669';
                                                                            e.currentTarget.style.transform = 'scale(1.02)';
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.backgroundColor = '#10b981';
                                                                            e.currentTarget.style.transform = 'scale(1)';
                                                                        }}
                                                                    >
                                                                        <FaEnvelope style={{ fontSize: '12px' }} />
                                                                        Email
                                                                    </a>
                                                                )}
                                                            </div>

                                                            {/* Contact Details */}
                                                            <div className="p-3 rounded-3 mb-3" style={{
                                                                backgroundColor: '#f8fafc',
                                                                border: '1px solid #e2e8f0'
                                                            }}>
                                                                {agent.email && (
                                                                    <div className="d-flex align-items-center mb-2">
                                                                        <div className="me-2 d-flex align-items-center justify-content-center" style={{
                                                                            width: '32px',
                                                                            height: '32px',
                                                                            borderRadius: '8px',
                                                                            backgroundColor: '#d1fae5',
                                                                            flexShrink: 0
                                                                        }}>
                                                                            <FaEnvelope style={{ fontSize: '13px', color: '#059669' }} />
                                                                        </div>
                                                                        <a 
                                                                            href={`mailto:${agent.email}`}
                                                                            className="text-decoration-none flex-grow-1"
                                    style={{ 
                                                                                color: '#475569',
                                                                                fontSize: '13px',
                                        fontWeight: '500'
                                    }}
                                                                            onMouseEnter={(e) => {
                                                                                e.currentTarget.style.color = '#059669';
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                e.currentTarget.style.color = '#475569';
                                                                            }}
                                                                        >
                                                                            {agent.email}
                                                                        </a>
                                                                    </div>
                                                                )}
                                                                {agent.phone_whatsapp && (
                                                                    <div className="d-flex align-items-center">
                                                                        <div className="me-2 d-flex align-items-center justify-content-center" style={{
                                                                            width: '32px',
                                                                            height: '32px',
                                                                            borderRadius: '8px',
                                                                            backgroundColor: '#dbeafe',
                                                                            flexShrink: 0
                                                                        }}>
                                                                            <FaPhone style={{ fontSize: '13px', color: '#2563eb' }} />
                                                                        </div>
                                                                        <a 
                                                                            href={`tel:${agent.phone_whatsapp}`}
                                                                            className="text-decoration-none flex-grow-1"
                                                                            style={{ 
                                                                                color: '#475569',
                                        fontSize: '13px',
                                                                                fontWeight: '500'
                                                                            }}
                                                                            onMouseEnter={(e) => {
                                                                                e.currentTarget.style.color = '#2563eb';
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                e.currentTarget.style.color = '#475569';
                                                                            }}
                                                                        >
                                                                            {agent.phone_whatsapp}
                                                                        </a>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Languages */}
                                                            {agent.languages_spoken && agent.languages_spoken.length > 0 && (
                                                                <div>
                                                                    <small className="text-uppercase fw-semibold d-block mb-2" style={{ 
                                                                        fontSize: '10px',
                                        fontWeight: '600',
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.8px',
                                                                        color: '#64748b'
                                                                    }}>
                                                                        Languages Spoken
                                                                    </small>
                                                                    <div className="d-flex flex-wrap gap-2">
                                                                        {agent.languages_spoken.map((lang, idx) => (
                                                                            <span 
                                                                                key={idx} 
                                                                                className="badge" 
                                                                                style={{ 
                                                                                    fontSize: '11px', 
                                                                                    padding: '6px 12px',
                                                                                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                                                                    color: '#2563eb',
                                                                                    border: '1px solid #bfdbfe',
                                                                                    fontWeight: '600',
                                                                                    borderRadius: '8px',
                                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                                                                }}
                                                                            >
                                                                                {lang}
                                    </span>
                                                                        ))}
                                </div>
                            </div>
                                                            )}
                                                    </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Company Information Section */}
                            {lot.auction?.company && (
                                <div className="card border-0" style={{ 
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0',
                                    overflow: 'hidden',
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                                }}>
                                    <div className="card-body p-0">
                                        <div
                                            className="p-4 border-0"
                                        style={{
                                                cursor: 'pointer',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                            }}
                                            onClick={() => setCompanyDrawerVisible(true)}
                                        onMouseEnter={(e) => {
                                                e.currentTarget.parentElement.style.transform = 'translateY(-2px)';
                                                e.currentTarget.parentElement.style.boxShadow = '0 8px 32px rgba(37, 99, 235, 0.35)';
                                        }}
                                        onMouseLeave={(e) => {
                                                e.currentTarget.parentElement.style.transform = 'translateY(0)';
                                                e.currentTarget.parentElement.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                                        }}
                                    >
                                            <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-4">
                                                {/* Company Logo */}
                                                <div style={{ flexShrink: 0 }}>
                                                    {lot.auction.company.company_logo_url ? (
                                                        <img
                                                            src={lot.auction.company.company_logo_url.startsWith('http')
                                                                ? lot.auction.company.company_logo_url
                                                                : env.BACKEND_URL + lot.auction.company.company_logo_url}
                                                            alt={lot.auction.company.company_name}
                                                            className="rounded-circle"
                                    style={{
                                                                width: '90px',
                                                                height: '90px',
                                                                objectFit: 'cover',
                                                                border: '4px solid rgba(255,255,255,0.25)',
                                                                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                                                                backgroundColor: 'rgba(255,255,255,0.1)'
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                                                            style={{
                                                                width: '90px',
                                                                height: '90px',
                                                                background: 'rgba(255,255,255,0.15)',
                                                                border: '4px solid rgba(255,255,255,0.25)',
                                                                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                                                                fontSize: '36px',
                                                                backdropFilter: 'blur(10px)'
                                                            }}
                                                        >
                                                            {lot.auction.company.company_name?.charAt(0).toUpperCase() || 'C'}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Company Info */}
                                                <div className="flex-grow-1 text-center text-md-start" style={{ minWidth: 0 }}>
                                                    <div className="d-flex flex-column align-items-center align-items-md-start mb-2">
                                                        <div style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '10px',
                                                            padding: '8px 16px',
                                                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                                            border: '2px solid rgba(255, 255, 255, 0.4)',
                                                            borderRadius: '12px',
                                                            backdropFilter: 'blur(10px)',
                                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                            marginBottom: '12px',
                                                            width: 'fit-content'
                                                        }}>
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '32px',
                                                                height: '32px',
                                                                borderRadius: '8px',
                                                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                                                backdropFilter: 'blur(10px)'
                                                            }}>
                                                                <FaStore style={{ fontSize: '16px', color: '#ffffff' }} />
                                                            </div>
                                                            <h5 className="mb-0 text-white fw-bold" style={{
                                                                fontSize: '16px',
                                                                letterSpacing: '1px',
                                                                textTransform: 'uppercase',
                                                                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                                                margin: 0
                                                            }}>
                                                                Company Profile
                                                            </h5>
                                                        </div>
                                                        <h4 className="mb-0 text-white fw-bold" style={{ 
                                                            fontSize: '24px',
                                                            letterSpacing: '-0.5px',
                                                            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                        }}>
                                                            {lot.auction.company.company_name || 'Company'}
                                                        </h4>
                                                    </div>
                                                    
                                                    {lot.auction.company.short_tagline && (
                                                        <p className="text-white mb-3" style={{ 
                                                            fontSize: '14px',
                                                            opacity: 0.95,
                                                            fontWeight: '500',
                                                            marginBottom: '12px',
                                                            lineHeight: '1.5'
                                                        }}>
                                                            {lot.auction.company.short_tagline}
                                                        </p>
                                                    )}

                                                    {/* Contact Information */}
                                                    <div className="d-flex flex-column flex-md-row flex-md-wrap gap-3 gap-md-4 align-items-center align-items-md-start">
                                                        {lot.auction.company.email_address && (
                                                            <div className="d-flex align-items-center" style={{ fontSize: '13px' }}>
                                                                <div className="me-2 d-flex align-items-center justify-content-center" style={{
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    borderRadius: '8px',
                                                                    backgroundColor: 'rgba(255,255,255,0.15)',
                                                                    flexShrink: 0,
                                                                    backdropFilter: 'blur(10px)'
                                                                }}>
                                                                    <FaEnvelope style={{ fontSize: '14px', color: '#ffffff' }} />
                                                                </div>
                                                                <a 
                                                                    href={`mailto:${lot.auction.company.email_address}`}
                                                                    className="text-white text-decoration-none"
                                                                    style={{ 
                                                                        fontSize: '13px',
                                                                        fontWeight: '500',
                                                                        opacity: 0.95
                                    }}
                                    onMouseEnter={(e) => {
                                                                        e.currentTarget.style.opacity = '1';
                                                                        e.currentTarget.style.textDecoration = 'underline';
                                    }}
                                    onMouseLeave={(e) => {
                                                                        e.currentTarget.style.opacity = '0.95';
                                                                        e.currentTarget.style.textDecoration = 'none';
                                                                    }}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    {lot.auction.company.email_address}
                                                                </a>
                                                            </div>
                                                        )}
                                                        {lot.auction.company.phone_whatsapp && (
                                                            <div className="d-flex align-items-center" style={{ fontSize: '13px' }}>
                                                                <div className="me-2 d-flex align-items-center justify-content-center" style={{
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    borderRadius: '8px',
                                                                    backgroundColor: 'rgba(255,255,255,0.15)',
                                                                    flexShrink: 0,
                                                                    backdropFilter: 'blur(10px)'
                                                                }}>
                                                                    <FaPhone style={{ fontSize: '14px', color: '#ffffff' }} />
                                                                </div>
                                                                <a 
                                                                    href={`tel:${lot.auction.company.phone_whatsapp}`}
                                                                    className="text-white text-decoration-none"
                                    style={{
                                                                        fontSize: '13px',
                                                                        fontWeight: '500',
                                                                        opacity: 0.95
                                    }}
                                    onMouseEnter={(e) => {
                                                                        e.currentTarget.style.opacity = '1';
                                                                        e.currentTarget.style.textDecoration = 'underline';
                                    }}
                                    onMouseLeave={(e) => {
                                                                        e.currentTarget.style.opacity = '0.95';
                                                                        e.currentTarget.style.textDecoration = 'none';
                                                                    }}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    {lot.auction.company.phone_whatsapp}
                                                                </a>
                                                            </div>
                                                        )}
                                                        {lot.auction.company.business_address && (
                                                            <div className="d-flex align-items-center" style={{ fontSize: '13px' }}>
                                                                <div className="me-2 d-flex align-items-center justify-content-center" style={{
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    borderRadius: '8px',
                                                                    backgroundColor: 'rgba(255,255,255,0.15)',
                                                                    flexShrink: 0,
                                                                    backdropFilter: 'blur(10px)'
                                                                }}>
                                                                    <FaMapMarkerAlt style={{ fontSize: '14px', color: '#ffffff' }} />
                                                                </div>
                                                                <span className="text-white" style={{ 
                                                                    fontSize: '13px',
                                                                    fontWeight: '500',
                                                                    opacity: 0.95
                                                                }}>
                                                                    {lot.auction.company.business_address}
                                                                </span>
                                                            </div>
                                    )}
                                                    </div>
                                                </div>
                                
                                                {/* View Details Button */}
                                             
                        </div>
                    </div>
                </div>
            </div>
                            )}
                </div>
            </div>

                    {/* Right Column - Details */}
                    <div className="col-lg-4">
                        <div 
                            className="d-flex flex-column gap-3"
                        >
                            {/* Current Price Section - Sticky */}
                            {shouldStick ? (
                                <Affix offsetTop={20} offsetBottom={20}>
                        <div className="card border-0" style={{ 
                            borderRadius: '16px', 
                                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', 
                                border: '2px solid #93c5fd !important',
                                    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
                                    zIndex: 10
                        }}>
                                <div className="card-body p-4">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <small className="text-uppercase fw-semibold d-block mb-2" style={{ 
                                                fontSize: '11px', 
                                                color: '#64748b',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Current Price
                                            </small>
                                            <h2 className="mb-0 fw-bold" style={{ 
                                                fontSize: '32px', 
                                                color: '#1e40af', 
                                                lineHeight: '1.2',
                                                letterSpacing: '-0.5px'
                                            }}>
                                                {formatCurrency(currentPrice || lot?.current_price || lot?.winning_bid?.amount || lot?.starting_price)}
                                            </h2>
                            </div>
                                            <div className="text-end">
                                            {lot?.increment && (
                                                <div className="mb-3">
                                                <small className="text-uppercase fw-semibold d-block mb-2" style={{ 
                                                    fontSize: '11px', 
                                                    color: '#64748b',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    Increment
                                                </small>
                                                <h5 className="mb-0 fw-bold" style={{ 
                                                    fontSize: '20px', 
                                                    color: '#059669',
                                                    letterSpacing: '-0.3px'
                                                }}>
                                                    {formatCurrency(lot.increment || lot.default_increment || 5)}
                                                </h5>
                        </div>
                                        )}
                                            <div>
                                                <small className="text-uppercase fw-semibold d-block mb-2" style={{ 
                                                    fontSize: '11px', 
                                                    color: '#64748b',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    Starting Price
                                                </small>
                                                <h5 className="mb-0 fw-bold" style={{ 
                                                    fontSize: '20px', 
                                                    color: '#3b82f6',
                                                    letterSpacing: '-0.3px'
                                                }}>
                                                    {formatCurrency(lot?.starting_price || 0)}
                                                </h5>
                                            </div>
                                        </div>
                    </div>

                                    {/* Animated Enter Auction Button */}
                                    {lot?.auction && (() => {
                                        const now = moment();
                                        const startTime = lot.auction?.start_time ? moment(lot.auction.start_time) : null;
                                        const isLiveOrActive = lot.auction?.status === 'live' || 
                                                              (lot.auction?.status === 'active' && startTime && !startTime.isAfter(now)) ||
                                                              countdownCompleted;
                                        
                                        if (!isLiveOrActive) return null;
                                        
                                        return (
                                            <button
                                                className="btn w-100 fw-bold text-white border-0 connect-to-bid-button live-bid-button"
                                                onClick={() => {
                                                    const newWindow = window.open(
                                                        `/live-auctions/${auctionSlug}`,
                                                        '_blank',
                                                        'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=1920,height=1080'
                                                    );
                                                    if (newWindow) {
                                                        newWindow.focus();
                                                    }
                                                }}
                            style={{
                                                    height: '56px',
                                                    fontSize: '15px',
                                                    fontWeight: 700,
                                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                    border: 'none',
                                                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    borderRadius: '12px',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.8px',
                                                    position: 'relative',
                                                    overflow: 'hidden'
                            }}
                        >
                                                <span style={{ marginRight: '8px', fontSize: '18px' }}>⚡</span>
                                                Enter Auction
                                            </button>
                                        );
                                    })()}
                                </div>
                            </div>
                                </Affix>
                            ) : (
                            <div className="card border-0" style={{ 
                                borderRadius: '16px', 
                                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', 
                                border: '2px solid #93c5fd !important',
                                    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
                                    zIndex: 10
                            }}>
                                <div className="card-body p-4">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <small className="text-uppercase fw-semibold d-block mb-2" style={{ 
                                                fontSize: '11px', 
                                                color: '#64748b',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Current Price
                                            </small>
                                            <h2 className="mb-0 fw-bold" style={{ 
                                                fontSize: '32px', 
                                                color: '#1e40af', 
                                                lineHeight: '1.2',
                                                letterSpacing: '-0.5px'
                                            }}>
                                                {formatCurrency(currentPrice || lot?.current_price || lot?.winning_bid?.amount || lot?.starting_price)}
                                            </h2>
                                        </div>
                                            <div className="text-end">
                                            {lot?.increment && (
                                                <div className="mb-3">
                                                <small className="text-uppercase fw-semibold d-block mb-2" style={{ 
                                                    fontSize: '11px', 
                                                    color: '#64748b',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    Increment
                                                </small>
                                                <h5 className="mb-0 fw-bold" style={{ 
                                                    fontSize: '20px', 
                                                    color: '#059669',
                                                    letterSpacing: '-0.3px'
                                                }}>
                                                    {formatCurrency(lot.increment || lot.default_increment || 5)}
                                                </h5>
                                            </div>
                                        )}
                                            <div>
                                                <small className="text-uppercase fw-semibold d-block mb-2" style={{ 
                                                    fontSize: '11px', 
                                                    color: '#64748b',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    Starting Price
                                                </small>
                                                <h5 className="mb-0 fw-bold" style={{ 
                                                    fontSize: '20px', 
                                                    color: '#3b82f6',
                                                    letterSpacing: '-0.3px'
                                                }}>
                                                    {formatCurrency(lot?.starting_price || 0)}
                                                </h5>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Animated Enter Auction Button */}
                                    {lot?.auction && (() => {
                                        const now = moment();
                                        const startTime = lot.auction?.start_time ? moment(lot.auction.start_time) : null;
                                        const isLiveOrActive = lot.auction?.status === 'live' || 
                                                              (lot.auction?.status === 'active' && startTime && !startTime.isAfter(now)) ||
                                                              countdownCompleted;
                                        
                                        if (!isLiveOrActive) return null;
                                        
                                        return (
                                            <button
                                                className="btn w-100 fw-bold text-white border-0 connect-to-bid-button live-bid-button"
                                                onClick={() => {
                                                    const newWindow = window.open(
                                                        `/live-auctions/${auctionSlug}`,
                                                        '_blank',
                                                        'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=1920,height=1080'
                                                    );
                                                    if (newWindow) {
                                                        newWindow.focus();
                                                    }
                                                }}
                                                style={{
                                                    height: '56px',
                                                    fontSize: '15px',
                                                    fontWeight: 700,
                                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                    border: 'none',
                                                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    borderRadius: '12px',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.8px',
                                                    position: 'relative',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <span style={{ marginRight: '8px', fontSize: '18px' }}>⚡</span>
                                                Enter Auction
                                            </button>
                                        );
                                    })()}
                                </div>
                            </div>
                            )}

                            {/* Lot Details Card - Sticky */}
                            {shouldStick ? (
                                <Affix offsetTop={200} offsetBottom={20}>
                            <div className="card border-0 d-none d-lg-block" style={{ 
                                borderRadius: '16px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0',
                                    zIndex: 9
                            }}>
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <h5 className="card-title mb-0 fw-bold" style={{ 
                                            fontSize: '20px', 
                                            color: '#1e293b',
                                            letterSpacing: '-0.3px'
                                        }}>
                                            <FaInfoCircle className="me-2" style={{ color: '#3b82f6' }} />
                                            Overview
                                        </h5>
                                        {/* <span className="badge px-3 py-2" style={{ 
                                            fontSize: '11px',
                                            backgroundColor: '#f1f5f9',
                                            color: '#475569',
                                            fontWeight: '600',
                                            border: '1px solid #e2e8f0'
                                        }}>
                                            Lot #{lot.id}
                                        </span> */}
                                    </div>

                                    {/* Starting Price - Featured */}
                                    <div className="p-4 mb-3 rounded-3" style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                        border: 'none',
                                        boxShadow: '0 4px 16px rgba(37, 99, 235, 0.25)'
                                    }}>
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <small className="text-white text-uppercase fw-semibold d-block mb-2" style={{ 
                                                    fontSize: '11px', 
                                                    letterSpacing: '1px', 
                                                    opacity: 0.95 
                                                }}>
                                                    Starting Price
                                                </small>
                                                <h3 className="mb-0 fw-bold text-white" style={{ 
                                                    fontSize: '30px', 
                                                    textShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                    letterSpacing: '-0.5px'
                                                }}>
                                                    {formatCurrency(lot.starting_price)}
                                                </h3>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Info Grid */}
                                    <div className="row g-2 mb-3">
                                        {/* Quantity */}
                                        <div className="col-6">
                                            <div className="p-3 rounded-3 h-100" style={{
                                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                                border: '1px solid #e2e8f0',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                            }}>
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="bg-blue-50 rounded-2 p-2" style={{
                                                        backgroundColor: '#eff6ff'
                                                    }}>
                                                        <FaHashtag style={{ fontSize: '16px', color: '#3b82f6' }} />
                                                    </div>
                                                </div>
                                                <small className="text-uppercase fw-semibold d-block mb-2" style={{ 
                                                    fontSize: '10px', 
                                                    letterSpacing: '0.8px',
                                                    color: '#64748b'
                                                }}>
                                                    Quantity
                                                </small>
                                                <h5 className="mb-0 fw-bold" style={{ 
                                                    fontSize: '22px', 
                                                    color: '#1e293b',
                                                    letterSpacing: '-0.3px'
                                                }}>
                                                    {lot.quantity}
                                                </h5>
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <div className="col-6">
                                            <div className={`p-3 rounded-3 h-100`} style={{
                                                background: getStatusColor(lot.status) === 'success'
                                                    ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                                                    : getStatusColor(lot.status) === 'danger'
                                                    ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                                                    : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                                                border: `2px solid ${getStatusColor(lot.status) === 'success' ? '#10b981' : getStatusColor(lot.status) === 'danger' ? '#ef4444' : '#cbd5e1'}`,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                            }}>
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="rounded-2 p-2" style={{
                                                        backgroundColor: getStatusColor(lot.status) === 'success' 
                                                            ? 'rgba(16, 185, 129, 0.15)' 
                                                            : getStatusColor(lot.status) === 'danger'
                                                            ? 'rgba(239, 68, 68, 0.15)'
                                                            : 'rgba(148, 163, 184, 0.15)'
                                                    }}>
                                                        <FaCheckCircle style={{ 
                                                            fontSize: '16px',
                                                            color: getStatusColor(lot.status) === 'success' ? '#10b981' : getStatusColor(lot.status) === 'danger' ? '#ef4444' : '#64748b'
                                                        }} />
                                                    </div>
                                                </div>
                                                <small className={`text-uppercase fw-semibold d-block mb-2`} style={{ 
                                                    fontSize: '10px', 
                                                    letterSpacing: '0.8px',
                                                    color: getStatusColor(lot.status) === 'success' ? '#059669' : getStatusColor(lot.status) === 'danger' ? '#dc2626' : '#64748b'
                                                }}>
                                                    Status
                                                </small>
                                                <h6 className="mb-0 fw-bold text-capitalize" style={{
                                                    fontSize: '18px',
                                                    color: getStatusColor(lot.status) === 'success' ? '#047857' : getStatusColor(lot.status) === 'danger' ? '#b91c1c' : '#334155',
                                                    letterSpacing: '-0.2px'
                                                }}>
                                                    {getDisplayStatus(lot.status)}
                                                </h6>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Details Section */}
                                    <div className="border-top pt-3 mt-3" style={{ borderColor: '#e2e8f0' }}>
                                        {/* <h6 className="text-uppercase fw-bold mb-3" style={{ 
                                            fontSize: '11px', 
                                            letterSpacing: '1.2px',
                                            color: '#64748b'
                                        }}>
                                            <FaFileAlt className="me-2" style={{ color: '#3b82f6' }} />
                                            Additional Details
                                        </h6>
                                         */}
                                        {/* Last Bid Time */}
                                        {lot.last_bid_time && (
                                            <div className="d-flex align-items-center mb-2 p-3 rounded-3" style={{ 
                                                backgroundColor: '#f8fafc',
                                                border: '1px solid #e2e8f0'
                                            }}>
                                                <div className="bg-blue-50 rounded-2 p-2 me-3" style={{
                                                    backgroundColor: '#eff6ff'
                                                }}>
                                                    <FaClock style={{ fontSize: '16px', color: '#3b82f6' }} />
                                                </div>
                                                <div className="flex-grow-1">
                                                    <small className="text-uppercase fw-semibold d-block mb-1" style={{ 
                                                        fontSize: '10px', 
                                                        letterSpacing: '0.8px',
                                                        color: '#64748b'
                                                    }}>
                                                        Last Bid Time
                                                    </small>
                                                    <span className="fw-semibold" style={{ 
                                                        fontSize: '15px', 
                                                        color: '#1e293b'
                                                    }}>
                                                        {moment(lot.last_bid_time).format('MMM DD, YYYY HH:mm')}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Has Active Bids */}
                                        {lot.has_bid && (
                                            <div className="d-flex align-items-center p-3 rounded-3" style={{
                                                background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                                                border: '2px solid #10b981',
                                                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)'
                                            }}>
                                                <div className="bg-green-50 rounded-2 p-2 me-3" style={{
                                                    backgroundColor: 'rgba(16, 185, 129, 0.15)'
                                                }}>
                                                    <FaCheckCircle style={{ fontSize: '16px', color: '#10b981' }} />
                                                </div>
                                                <div className="flex-grow-1">
                                                    <small className="text-uppercase fw-semibold d-block mb-1" style={{ 
                                                        fontSize: '10px', 
                                                        letterSpacing: '0.8px',
                                                        color: '#059669'
                                                    }}>
                                                        Active Bids
                                                    </small>
                                                    <span className="fw-bold" style={{ 
                                                        fontSize: '18px',
                                                        color: '#047857'
                                                    }}>
                                                        Yes
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                                </Affix>
                            ) : (
                        <div className="card border-0 d-none d-lg-block" style={{ 
                            borderRadius: '16px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e2e8f0',
                                    zIndex: 9
                        }}>
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <h5 className="card-title mb-0 fw-bold" style={{ 
                                            fontSize: '20px', 
                                        color: '#1e293b',
                                            letterSpacing: '-0.3px'
                                    }}>
                                            <FaInfoCircle className="me-2" style={{ color: '#3b82f6' }} />
                                            Overview
                                        </h5>
                                        <span className="badge px-3 py-2" style={{ 
                                            fontSize: '11px',
                                            backgroundColor: '#f1f5f9',
                                        color: '#475569',
                                            fontWeight: '600',
                                            border: '1px solid #e2e8f0'
                                    }}>
                                            Lot #{lot.id}
                                        </span>
                </div>

                                    {/* Starting Price - Featured */}
                                    <div className="p-4 mb-3 rounded-3" style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                        border: 'none',
                                        boxShadow: '0 4px 16px rgba(37, 99, 235, 0.25)'
                                    }}>
                                        <div className="d-flex align-items-center">
                                            <div className="me-3" style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <FaDollarSign className="text-white" style={{ fontSize: '24px' }} />
                                </div>
                                            <div className="flex-grow-1">
                                                <small className="text-white text-uppercase fw-semibold d-block mb-2" style={{ 
                                                    fontSize: '11px', 
                                                    letterSpacing: '1px', 
                                                    opacity: 0.95 
                                                }}>
                                                    Starting Price
                                                </small>
                                                <h3 className="mb-0 fw-bold text-white" style={{ 
                                                    fontSize: '30px', 
                                                    textShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                    letterSpacing: '-0.5px'
                                                }}>
                                                    {formatCurrency(lot.starting_price)}
                                                </h3>
                            </div>
                        </div>
                    </div>

                                    {/* Info Grid */}
                                    <div className="row g-2 mb-3">
                                        {/* Quantity */}
                                        <div className="col-6">
                                            <div className="p-3 rounded-3 h-100" style={{
                                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                border: '1px solid #e2e8f0',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                            }}>
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="bg-blue-50 rounded-2 p-2" style={{
                                                        backgroundColor: '#eff6ff'
                                                    }}>
                                                        <FaHashtag style={{ fontSize: '16px', color: '#3b82f6' }} />
                                                    </div>
                                            </div>
                                                <small className="text-uppercase fw-semibold d-block mb-2" style={{ 
                                                    fontSize: '10px', 
                                                    letterSpacing: '0.8px',
                                                    color: '#64748b'
                                                }}>
                                                    Quantity
                                                </small>
                                                <h5 className="mb-0 fw-bold" style={{ 
                                                    fontSize: '22px', 
                                                    color: '#1e293b',
                                                    letterSpacing: '-0.3px'
                                                }}>
                                                    {lot.quantity}
                                                </h5>
                                                </div>
                                                </div>

                                        {/* Status */}
                                        <div className="col-6">
                                            <div className={`p-3 rounded-3 h-100`} style={{
                                                background: getStatusColor(lot.status) === 'success'
                                                    ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                                                    : getStatusColor(lot.status) === 'danger'
                                                    ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                                                    : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                                                border: `2px solid ${getStatusColor(lot.status) === 'success' ? '#10b981' : getStatusColor(lot.status) === 'danger' ? '#ef4444' : '#cbd5e1'}`,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                            }}>
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="rounded-2 p-2" style={{
                                                        backgroundColor: getStatusColor(lot.status) === 'success' 
                                                            ? 'rgba(16, 185, 129, 0.15)' 
                                                            : getStatusColor(lot.status) === 'danger'
                                                            ? 'rgba(239, 68, 68, 0.15)'
                                                            : 'rgba(148, 163, 184, 0.15)'
                                                    }}>
                                                        <FaCheckCircle style={{ 
                                                            fontSize: '16px',
                                                            color: getStatusColor(lot.status) === 'success' ? '#10b981' : getStatusColor(lot.status) === 'danger' ? '#ef4444' : '#64748b'
                                                        }} />
                                            </div>
                                            </div>
                                                <small className={`text-uppercase fw-semibold d-block mb-2`} style={{ 
                                                    fontSize: '10px', 
                                                    letterSpacing: '0.8px',
                                                    color: getStatusColor(lot.status) === 'success' ? '#059669' : getStatusColor(lot.status) === 'danger' ? '#dc2626' : '#64748b'
                                                }}>
                                                    Status
                                                </small>
                                                <h6 className="mb-0 fw-bold text-capitalize" style={{
                                                    fontSize: '18px',
                                                    color: getStatusColor(lot.status) === 'success' ? '#047857' : getStatusColor(lot.status) === 'danger' ? '#b91c1c' : '#334155',
                                                    letterSpacing: '-0.2px'
                                                }}>
                                                    {getDisplayStatus(lot.status)}
                                                </h6>
                                        </div>
                                    </div>
                                </div>

                                    {/* Details Section */}
                                    <div className="border-top pt-3 mt-3" style={{ borderColor: '#e2e8f0' }}>
                                        <h6 className="text-uppercase fw-bold mb-3" style={{ 
                                            fontSize: '11px', 
                                            letterSpacing: '1.2px',
                                            color: '#64748b'
                                        }}>
                                            <FaFileAlt className="me-2" style={{ color: '#3b82f6' }} />
                                            Additional Details
                                        </h6>
                                        
                                        {/* Last Bid Time */}
                                        {lot.last_bid_time && (
                                            <div className="d-flex align-items-center mb-2 p-3 rounded-3" style={{ 
                                                backgroundColor: '#f8fafc',
                                                border: '1px solid #e2e8f0'
                                            }}>
                                                <div className="bg-blue-50 rounded-2 p-2 me-3" style={{
                                                    backgroundColor: '#eff6ff'
                                                }}>
                                                    <FaClock style={{ fontSize: '16px', color: '#3b82f6' }} />
                            </div>
                                                <div className="flex-grow-1">
                                                    <small className="text-uppercase fw-semibold d-block mb-1" style={{ 
                                                        fontSize: '10px', 
                                                        letterSpacing: '0.8px',
                                                        color: '#64748b'
                                                    }}>
                                                        Last Bid Time
                                                    </small>
                                                    <span className="fw-semibold" style={{ 
                                                        fontSize: '15px', 
                                                        color: '#1e293b'
                                                    }}>
                                                        {moment(lot.last_bid_time).format('MMM DD, YYYY HH:mm')}
                                                    </span>
                        </div>
                    </div>
                )}

                                        {/* Has Active Bids */}
                                        {lot.has_bid && (
                                            <div className="d-flex align-items-center p-3 rounded-3" style={{
                                                background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                                                border: '2px solid #10b981',
                                                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)'
                                            }}>
                                                <div className="bg-green-50 rounded-2 p-2 me-3" style={{
                                                    backgroundColor: 'rgba(16, 185, 129, 0.15)'
                                                }}>
                                                    <FaCheckCircle style={{ fontSize: '16px', color: '#10b981' }} />
                                        </div>
                                                <div className="flex-grow-1">
                                                    <small className="text-uppercase fw-semibold d-block mb-1" style={{ 
                                                        fontSize: '10px', 
                                                        letterSpacing: '0.8px',
                                                        color: '#059669'
                                                    }}>
                                                        Active Bids
                                                    </small>
                                                    <span className="fw-bold" style={{ 
                                                        fontSize: '18px',
                                                        color: '#047857'
                                                    }}>
                                                        Yes
                                                    </span>
                                        </div>
                                    </div>
                                                            )}
                                                        </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

            </div>

            {/* Video Preview Modal */}
            {lot.video_url && (
                <VideoPreviewModal
                    visible={videoModalVisible}
                    onClose={() => setVideoModalVisible(false)}
                    videoUrl={lot.video_url}
                    title="Lot Video Preview"
                    width={900}
                    height={500}
                />
            )}

            {/* Company Drawer */}
            {lot.auction?.company && (
                <CompanyDrawer
                    visible={companyDrawerVisible}
                    onClose={() => setCompanyDrawerVisible(false)}
                    companyData={lot.auction.company}
                    userId={lot.auction.user_id}
                />
            )}

            {/* Share Auction Modal */}
            {lot?.auction && (
                <ShareAuctionModal
                    visible={shareModalVisible}
                    onClose={() => setShareModalVisible(false)}
                    auction={lot.auction}
                />
            )}

        </div>
    );
};

export default LotDetail;
